import { canvasToCompressedFile } from '../../utils/compress';

// Capture of a video frame into a JPEG File, extracted from the former
// Archive page (`drawCoveredVideoFrameToCanvas` + `handleClickCaptureCamera`).
//
// With `cover`, it crops the frame to the visible part of the <video> (which
// in the full-screen view is painted with `object-fit: cover`): what is
// saved is what the user framed. Then it downscales to 1600 px wide at
// most and compresses to JPEG 0.85, which is what was sent until now.

const MAX_WIDTH = 1600;
const JPEG_QUALITY = 0.85;

const drawFrame = (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  cover: boolean,
): boolean => {
  const context = canvas.getContext('2d');
  if (!context || !video.videoWidth || !video.videoHeight) return false;

  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;

  let sx = 0;
  let sy = 0;
  let sWidth = sourceWidth;
  let sHeight = sourceHeight;

  if (cover) {
    const { width: containerWidth, height: containerHeight } =
      video.getBoundingClientRect();
    const sourceRatio = sourceWidth / sourceHeight;
    const containerRatio = containerWidth / containerHeight;

    if (containerWidth && containerHeight) {
      if (sourceRatio > containerRatio) {
        sWidth = sourceHeight * containerRatio;
        sx = (sourceWidth - sWidth) / 2;
      } else if (sourceRatio < containerRatio) {
        sHeight = sourceWidth / containerRatio;
        sy = (sourceHeight - sHeight) / 2;
      }
    }
  }

  canvas.width = Math.round(sWidth);
  canvas.height = Math.round(sHeight);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    video,
    sx,
    sy,
    sWidth,
    sHeight,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return true;
};

const shrink = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
  if (canvas.width <= MAX_WIDTH) return canvas;

  const ratio = MAX_WIDTH / canvas.width;
  const resized = document.createElement('canvas');
  resized.width = Math.round(canvas.width * ratio);
  resized.height = Math.round(canvas.height * ratio);

  const context = resized.getContext('2d');
  if (!context) return canvas;

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(canvas, 0, 0, resized.width, resized.height);

  return resized;
};

export const captureFrame = async (
  video: HTMLVideoElement,
  options: { cover: boolean; filename?: string },
): Promise<File | null> => {
  const canvas = document.createElement('canvas');
  if (!drawFrame(video, canvas, options.cover)) return null;

  return canvasToCompressedFile(
    shrink(canvas),
    options.filename ?? `capture_${Date.now()}.jpg`,
    'image/jpeg',
    JPEG_QUALITY,
  );
};
